<template>
    <div class="animated fadeIn">
        <LoadingWidget
            v-if="initStatus != 1"
            :status="initStatus"
            @init="init"
        />
        <b-card v-else>
            <h5 class="mb-0">
                <i class="fa fa-fw fa-search mr-2" />Daftar Temuan Emas
            </h5>
            <hr />
            <b-row>
                <b-col md="3">
                    <b-form-group label="No. Faktur" class="mb-0">
                        <b-input-group>
                            <b-form-input
                                v-model="filter.no_faktur"
                                maxlength="50"
                                @keydown.enter="getItems"
                                placeholder="Cari No. Faktur..."
                            />
                        </b-input-group>
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="Status Temuan">
                        <b-form-select
                            v-model="filter.status"
                            :options="optStatus"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="Sumber">
                        <b-form-select
                            v-model="filter.source"
                            :options="optOriginType"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="Aksi">
                        <div
                            class="d-flex flex-nowrap d-flex justify-content-between"
                        >
                            <b-button variant="primary" @click="getItems">
                                <i class="fa fa-search mr-2" />Cari
                            </b-button>
                            <b-button
                                variant="danger"
                                class="ml-2"
                                @click="resetFilter"
                            >
                                Reset
                            </b-button>
                        </div>
                    </b-form-group>
                </b-col>
            </b-row>

            <b-table
                bordered
                hover
                show-empty
                no-local-sorting
                :busy="isBusy"
                :items="items"
                :fields="fields"
                responsive
            >
                <template v-slot:empty>
                    Tidak ada temuan emas.
                </template>
                <template v-slot:cell(no_faktur)="row">
                    {{ row.item.no_faktur || "-" }}
                </template>
                <template v-slot:cell(origin)="row">
                    Kadar:
                    <strong>{{ row.item.origin_purity || "-" }}</strong>
                    <br />
                    Berat Kotor:
                    <strong>{{ row.item.origin_dry_weight || "-" }} gr</strong>
                    <br />
                    Berat Potongan:
                    <strong
                        >{{
                            row.item.origin_weight_reduction || "-"
                        }}
                        gr</strong
                    >
                    <br />
                    <strong>{{ row.item.source || "-" }}</strong>
                    <b-badge
                        v-if="row.item.reset_request_id"
                        variant="warning"
                        class="ml-1"
                        >EDIT</b-badge
                    >
                    <template v-if="row.item.created_by && row.item.created_at">
                        <div class="small text-muted">
                            {{ row.item.created_by }} -
                            {{ parseDateTime(row.item.created_at) }}
                        </div>
                    </template>
                </template>
                <template v-slot:cell(sm)="row">
                    Kadar XRF:
                    <strong>{{ row.item.sm_purity || "-" }}</strong>
                    <br />
                    Berat Kotor:
                    <strong>{{ row.item.sm_dry_weight || "-" }} gr</strong>
                    <br />
                    Berat Potongan:
                    <strong
                        >{{ row.item.sm_weight_reduction || "-" }} gr</strong
                    >
                </template>
                <template v-slot:cell(ops)="row">
                    Hasil:
                    <template v-if="row.item.ops_accepted !== null">
                        <b-badge
                            :variant="
                                row.item.ops_accepted ? 'success' : 'danger'
                            "
                        >
                            {{
                                row.item.ops_accepted ? "DITERIMA" : "DISANGGAH"
                            }}
                        </b-badge>
                    </template>
                    <template v-else>
                        -
                    </template>
                    <div class="small text-muted" v-if="row.item.ops_result_at">
                        {{ row.item.ops_result_by_name }} -
                        {{ parseDateTime(row.item.ops_result_at) }}
                    </div>
                </template>
                <template v-slot:cell(final)="row">
                    Kadar:
                    <strong>{{ row.item.final_purity || "-" }}</strong>
                    <br />
                    Berat Kotor:
                    <strong>{{ row.item.final_dry_weight || "-" }} gr</strong>
                    <br />
                    Berat Potongan:
                    <strong
                        >{{ row.item.final_weight_reduction || "-" }} gr</strong
                    >
                    <div v-if="row.item.final_at" class="small text-muted">
                        {{ row.item.final_by }} -
                        {{ parseDateTime(row.item.final_at) }}
                    </div>
                </template>
                <template v-slot:cell(status)="row">
                    <b-badge
                        :variant="getStatusBadge(row.item.status).badge"
                        :class="getStatusBadge(row.item.status).class"
                    >
                        {{ getStatusBadge(row.item.status).text }}
                    </b-badge>
                </template>
                <template v-slot:cell(completed)="row">
                    <template v-if="row.item.completed_at">
                        Pada:
                        <strong>{{
                            parseDateTime(row.item.completed_at)
                        }}</strong>
                        <br />
                        Oleh:
                        <strong>{{ row.item.completed_by || "-" }}</strong>
                    </template>
                    <template v-else
                        >-</template
                    >
                </template>

                <template v-slot:cell(action)="row">
                    <router-link :to="'/emas/temuan/' + row.item.id">
                        <b-button
                            size="sm"
                            variant="primary"
                            v-b-tooltip.hover.top
                            title="Detail Temuan"
                        >
                            <i class="fa fa-fw fa-info" />
                        </b-button>
                    </router-link>
                </template>
            </b-table>
            <PageSizer
                :totalRows="totalRows"
                :page="currentPage"
                :options="options"
                @limit-change="onLimitChange"
                @page-change="onPageChange"
            />
        </b-card>
    </div>
</template>

<script>
export default {
    data() {
        return {
            initStatus: 0,
            isSubmitting: false,
            isBusy: false,

            items: [],

            filter: {
                no_faktur: null,
                status: null,
                source: null
            },

            fields: [
                { key: "no_faktur", label: "No Faktur", sortable: false },
                { key: "origin", label: "Sumber", sortable: false },
                {
                    key: "sm",
                    label: "SM",
                    sortable: false
                },
                { key: "ops", label: "Operasional", sortable: false },
                { key: "final", label: "Final", sortable: false },
                {
                    key: "status",
                    label: "Status",
                    sortable: false,
                    thClass: "text-center",
                    tdClass: "text-center"
                },
                { key: "completed", label: "Completed", sortable: false },
                { key: "action", label: "Aksi", sortable: false }
            ],

            options: [
                { value: 10, text: "10" },
                { value: 20, text: "20" },
                { value: 30, text: "30" },
                { value: 40, text: "40" },
                { value: 50, text: "50" }
            ],

            optStatus: [
                { value: null, text: "Semua" },
                { value: 1, text: "MENUNGGU PENERIMAAN" },
                { value: 2, text: "MENUNGGU AUDIT" },
                { value: 4, text: "DALAM DISKUSI" },
                { value: 3, text: "DISETUJUI AUDIT" },
                { value: 99, text: "SELESAI" }
            ],

            optOriginType: [
                { value: null, text: "Semua" },
                { value: "PEI", text: "PEI" },
                { value: "RBE", text: "RBE" },
                { value: "PGI", text: "PGI" },
                { value: "SGP", text: "SGP" }
            ],

            currentPage: 1,
            perPage: 10,
            totalRows: 0,

            selectedItem: null
        };
    },

    methods: {
        init() {
            this.initStatus = 0;
            this.getItems();
            this.initStatus = 1;
        },

        getItems(page) {
            this.isBusy = true;

            if (isNaN(page)) {
                this.currentPage = 1;
            } else {
                this.currentPage = page;
            }

            let param = this.$helper.getBasePagedQuery(
                this.currentPage,
                this.perPage,
                true
            );

            if (this.filter.no_faktur) {
                param += `&filterNoFaktur=${this.filter.no_faktur}`;
            }

            if (this.filter.status) {
                param += `&filterStatus=${this.filter.status}`;
            }
            if (this.filter.source) {
                param += `&source=${this.filter.source}`;
            }

            return this.$axios
                .get("gold-receipt-variances" + param)
                .then(res => {
                    this.items = res.data.data.items;
                    this.totalRows = res.data.data.total;
                    localStorage.setItem(
                        "listTemuanEmasFilter",
                        JSON.stringify(this.filter)
                    );
                })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => {
                    this.isBusy = false;
                });
        },

        onLimitChange(newPerPage) {
            this.perPage = newPerPage;
            this.getItems(this.currentPage);
        },

        onPageChange(newPage) {
            this.currentPage = newPage;
            this.getItems(this.currentPage);
        },

        getStatusBadge(status) {
            switch (status) {
                case 1:
                    return {
                        badge: "warning",
                        text: "MENUNGGU PENERIMAAN"
                    };
                case 2:
                    return {
                        badge: "secondary",
                        text: "MENUNGGU AUDIT"
                    };
                case 3:
                    return {
                        badge: "primary",
                        text: "DISETUJUI AUDIT",
                        class: "text-white"
                    };
                case 4:
                    return {
                        badge: "info",
                        text: "DALAM DISKUSI",
                        class: "text-white"
                    };
                case 99:
                    return {
                        badge: "success",
                        text: "SELESAI"
                    };
                default:
                    return {
                        badge: "light",
                        text: "-"
                    };
            }
        },
        resetFilter() {
            this.filter = {
                no_faktur: null,
                status: null,
                source: null
            };
            this.getItems();
        },

        parseDateTime(date) {
            return (
                this.$helper.parseDate(date) +
                " " +
                this.$helper.parseHourMinute(date)
            );
        }
    },

    created() {
        if (
            !this.$helper.isBlank(localStorage.getItem("listTemuanEmasFilter"))
        ) {
            this.filter = JSON.parse(
                localStorage.getItem("listTemuanEmasFilter")
            );
        }
        this.init();
    }
};
</script>

<style></style>
